import { defineStore } from 'pinia'
import { computed } from 'vue'
import { createSyncedRows, type RemoteSettings } from '../services/sync/syncedRows'
import { deviceId } from './writingLog'

// What is logged by hand for goals: check-ins, counts, and writing days marked
// done away from the app. Each device keeps one row per month and records
// changes as amounts added or taken away, so a row only ever has one writer.
// A day's value is the sum across devices, which lets one device undo a
// check-in made on another without either overwriting the other.

export interface GoalLogMonth {
  /** Keyed by day, 'YYYY-MM-DD', then by goal id. */
  days: Record<string, Record<string, number>>
}

const DAY = /^\d{4}-\d{2}-\d{2}$/

function parseMonth(raw: any): GoalLogMonth | null {
  if (!raw || typeof raw.days !== 'object' || raw.days === null) return null
  const days: GoalLogMonth['days'] = {}
  for (const [day, goals] of Object.entries<any>(raw.days)) {
    if (!DAY.test(day) || !goals || typeof goals !== 'object') continue
    const entries: Record<string, number> = {}
    for (const [goalId, value] of Object.entries(goals)) {
      if (typeof value === 'number' && Number.isFinite(value) && value !== 0) entries[goalId] = value
    }
    if (Object.keys(entries).length) days[day] = entries
  }
  return { days }
}

export const useGoalLogStore = defineStore('goalLog', () => {
  const device = deviceId()
  const rows = createSyncedRows<GoalLogMonth>({
    storageKey: 'ink-goal-log',
    remotePrefix: 'goal.log.',
    parse: parseMonth,
  })

  /**
   * Goal id → day → value, summed across devices. A sum can dip below zero
   * when two devices clear the same check-in at once; it reads as zero, and
   * the raw sum is kept so the next change still lands.
   */
  const totals = computed(() => {
    const byGoal = new Map<string, Map<string, number>>()
    for (const month of Object.values(rows.values.value)) {
      for (const [day, goals] of Object.entries(month.days)) {
        for (const [goalId, value] of Object.entries(goals)) {
          let days = byGoal.get(goalId)
          if (!days) byGoal.set(goalId, (days = new Map()))
          days.set(day, (days.get(day) ?? 0) + value)
        }
      }
    }
    return byGoal
  })

  function valueOn(goalId: string, day: string): number {
    return Math.max(0, rawOn(goalId, day))
  }

  function rawOn(goalId: string, day: string): number {
    return totals.value.get(goalId)?.get(day) ?? 0
  }

  /** Days with anything logged for a goal, earliest first. */
  function daysFor(goalId: string): string[] {
    return [...(totals.value.get(goalId)?.entries() ?? [])]
      .filter(([, value]) => value > 0)
      .map(([day]) => day)
      .sort()
  }

  /** Adds to (or, negative, takes from) a goal's value for a day, on this device's row. */
  function add(goalId: string, day: string, amount: number) {
    if (!amount || !DAY.test(day)) return
    const rowId = `${device}.${day.slice(0, 7)}`
    const current = rows.values.value[rowId] ?? { days: {} }
    const goals = { ...(current.days[day] ?? {}) }
    const next = (goals[goalId] ?? 0) + amount
    if (next === 0) delete goals[goalId]
    else goals[goalId] = next
    const days = { ...current.days }
    if (Object.keys(goals).length) days[day] = goals
    else delete days[day]
    rows.write(rowId, { days })
  }

  /** Sets a goal's value for a day, whatever devices logged before. */
  function setValue(goalId: string, day: string, value: number) {
    add(goalId, day, Math.max(0, Math.round(value)) - rawOn(goalId, day))
  }

  /** Checks a day in, or clears it if already checked in. */
  function toggle(goalId: string, day: string) {
    setValue(goalId, day, valueOn(goalId, day) > 0 ? 0 : 1)
  }

  async function syncWithRemote(remote: RemoteSettings) {
    await rows.syncWithRemote(remote)
  }

  return { totals, valueOn, daysFor, add, setValue, toggle, syncWithRemote }
})
